import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { map, values, filter, reduce } from 'ramda';
import moment from 'moment';

import DatePicker from 'material-ui/DatePicker';
import TimePicker from 'material-ui/TimePicker';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import IconButton from 'material-ui/IconButton';
import Toggle from 'material-ui/Toggle';
import BackIcon from 'material-ui/svg-icons/navigation/arrow-back';
import ClearIcon from 'material-ui/svg-icons/content/clear';
import DownIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-down';
import UpIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-up';
import PreviewIcon from 'material-ui/svg-icons/image/remove-red-eye';

import { Section, SectionItem } from '../basic/section';
import RichTextEditor from '../basic/RichTextEditor';
import Button from '../basic/Button';
import TextField from '../basic/TextField';
import OpportunityTags from '../basic/OpportunityTags';
import TextTag from './TextTag.js';
import Overlay from '../basic/Overlay';
import { createOpportunity, updateOpportunity } from '../../services/Api';

import UploadModal from '../../containers/modals/FileUploadModal';
import { LANGUAGES } from '../../constants/Constants';
import { EMediaType, EOpportunityType } from '../../constants/Enums';

import './Form.css';
import './OpportunityBuilderForm.css';

const validateType = values => {
  const errors = {
    type: null
  };
  if (!values.type) {
    errors.type = 'Type is required';
  }
  return errors;
};

const validateContentLink = values => {
  const errors = {
    contentUrl: null,
    contentType: null
  };
  if (values.contentType && !values.contentUrl) {
    errors.contentUrl = 'Link to content required if type is set';
  }
  if (!values.contentType && values.contentUrl) {
    errors.contentType = 'Content type required if link is set';
  }
  return errors;
};

const validateContentLanguage = values => {
  if (!values.contentLang) {
    return { contentLang: 'Content language is required' };
  }
  return { contentLang: null };
};

const validateTitle = values => {
  if (!values.postTitle) {
    return { postTitle: 'Displayed title is required' };
  }
  if (values.postTitle.length < 3) {
    return { postTitle: 'Must be at least 3 characters long' };
  }
  return { postTitle: null };
};

const validateMediaLink = values => {
  const errors = {
    mediaUrl: null,
    mediaType: null
  };
  if (values.mediaType && !values.mediaUrl) {
    errors.mediaUrl = 'Media link is required if type is set';
  } else if (values.mediaUrl && !values.mediaType) {
    errors.mediaType = 'Media type is required if link is set';
  }
  return errors;
};

const validateLang = values => {
  const errors = {
    displayedLang: null
  };
  if (!values.displayedLang) {
    errors.displayedLang = 'Displayed language is required';
  }
  return errors;
};

const toTimestamp = (date, time) => {
  if (!date) {
    return null;
  }
  const tm = time ? moment(time) : null;
  return moment(date)
    .set('hour', tm ? tm.get('hour') : 0)
    .set('minute', tm ? tm.get('minute') : 0)
    .toDate();
};

const parseTimestamp = timestamp => {
  if (!timestamp) {
    return {
      date: null,
      time: null
    };
  }
  const m = moment(timestamp);
  const hour = m.get('hour');
  const minute = m.get('minute');
  return {
    date: m.toDate(),
    time: hour === 0 && minute === 0 ? null : m.toDate()
  };
};

const validateValidityDates = values => {
  const startDate = values.validFrom;
  const endDate = values.validTo;
  const errors = {
    validFrom: null,
    validTo: null
  };
  if (!startDate && values.validFromTime) {
    errors.validFrom = 'Time is set without a date';
  }
  if (!endDate && values.validToTime) {
    errors.validTo = 'Time is set without a date';
  }
  const startTimestamp = startDate && toTimestamp(startDate, values.validFromTime);
  const endTimestamp = endDate && toTimestamp(endDate, values.validToTime);
  if (startTimestamp && endTimestamp && startTimestamp >= endTimestamp) {
    errors.validFrom = 'The beginning date must be before the end date!';
  }
  return errors;
};

const validateDisplayedDates = values => {
  const fromDate = values.displayedFrom;
  const toDate = values.displayedTo;
  const errors = {
    displayedFrom: null,
    displayedTo: null
  };
  if (fromDate && toDate && fromDate >= toDate) {
    errors.displayedFrom = 'The displayed from date must be set before the displayed to date';
  }
  return errors;
};

const validate = (values, options) => {
  return {
    ...validateType(values, options),
    ...validateContentLink(values, options),
    ...validateContentLanguage(values, options),
    ...validateTitle(values, options),
    ...validateMediaLink(values, options),
    ...validateLang(values, options),
    ...validateValidityDates(values, options),
    ...validateDisplayedDates(values, options)
  };
};

const formValid = errors => {
  return reduce((res, val) => res && !val, true, values(errors));
};

const withEmptyMenuItem = arr => [<MenuItem key="EMPTY" />, ...arr];

const renderTypes = () => {
  return withEmptyMenuItem(
    map(type => <MenuItem key={type.key} value={type.key} primaryText={type.label} />, values(EOpportunityType))
  );
};

const renderLanguages = () => {
  return withEmptyMenuItem(
    map(lang => <MenuItem key={lang.value} value={lang.value} primaryText={lang.label} />, LANGUAGES)
  );
};

const renderMediaTypes = (handleOpenFileUpload, supports) => {
  return withEmptyMenuItem(
    map(
      type => (
        <MenuItem
          key={type.key}
          value={type.key}
          primaryText={type.label}
          onClick={type.supportsUpload && handleOpenFileUpload}
        />
      ),
      filter(v => !supports || supports(v), values(EMediaType))
    )
  );
};

const renderCompanies = companies => {
  return withEmptyMenuItem(
    map(c => <MenuItem key={c.id} value={c.id} primaryText={c.name} secondaryText={c.slug} />, companies)
  );
};

const renderOverlayContent = data => {
  const encodedData = encodeURIComponent(JSON.stringify(data));
  const location = window.location;
  const baseUrl =
    location.hostname === 'localhost'
      ? `http://localhost:3001`
      : `https://${location.host.replace(/admin/g, 'welcome')}`;
  const url = `${baseUrl}/public/opportunities/preview?p=${encodedData}`;
  return <iframe title="Preview" src={url} style={{ width: '100%', height: '100%', border: 'none', background: 'white' }} />;
};

const initialState = {
  modification: false,
  // Location
  expandedLocation: false,
  contentUploadOpen: false,
  mediaUploadOpen: false,
  // OPPORTUNITY
  opportunityTags: [],
  type: null,
  contentUrl: null,
  contentType: null,
  contentLang: null,
  validFrom: null,
  validTo: null,
  free: false,
  location: null,
  // POST
  postTitle: null,
  mediaUrl: null,
  mediaType: null,
  postDescription: null,
  displayedFrom: null,
  displayedTo: null,
  postTags: [],
  displayedLang: null,
  publishedAt: null,

  meta: {
    purePostDescriptionShown: false
  },
  errors: {
    type: '',
    contentUrl: '',
    contentType: '',
    contentLang: '',
    validFrom: '',
    validTo: '',
    free: '',
    location: '',
    postTitle: '',
    mediaUrl: '',
    mediaType: '',
    postDescription: '',
    displayedFrom: '',
    displayedTo: '',
    postTags: '',
    displayedLang: ''
  },
  previewOpen: false
};

const parseText = t => t || null;
const formatText = t => t || '';
const formatDate = d => d && new Date(d);

const renderShowPlainDescription = (shown, handleSetShown) => {
  const iconStyle = { width: 16, height: 16, fill: '#dd4b64', verticalAlign: 'middle' };
  return (
    <div style={{ marginTop: '8px', color: '#dd4b64', cursor: 'pointer', textAlign: 'right', padding: '0 8px' }}>
      <small>
        <a role="button" onClick={() => handleSetShown(!shown)}>
          <span style={{ verticalAlign: 'middle' }}>{shown ? 'Hide' : 'Show plain text editor'}</span>&nbsp;
          {shown ? <UpIcon style={iconStyle} /> : <DownIcon style={iconStyle} />}
        </a>
      </small>
    </div>
  );
};

const MediaPreview = props => {
  const { type, url } = props;
  if (!type || !url) {
    //noinspection JSConstructorReturnsPrimitive
    return null;
  }
  let content;
  if (type === EMediaType.IMAGE.key) {
    content = <img src={url} style={{ maxWidth: '100%' }} alt={url} />;
  } else if (type === EMediaType.VIDEO.key) {
    content = (
      <video preload="auto" style={{ maxWidth: '100%' }} controls>
        <source src={url} />
        <strong>Your browser does not support video.</strong>
      </video>
    );
  } else {
    content = (
      <div style={{ padding: '16px' }}>
        <small>
          <em>No preview available</em>
        </small>
      </div>
    );
  }
  return (
    <div>
      <div className="floating-label">Preview</div>
      <div
        style={{
          background: 'rgba(0,0,0,0.05)',
          borderRadius: '2px',
          overflow: 'hidden',
          margin: '8px 0 0',
          textAlign: 'center',
          fontSize: 0
        }}
      >
        {content}
      </div>
    </div>
  );
};

const DateField = props => {
  const { className, label, value, onChange, ...rest } = props;
  return (
    <div className="flex flex1 flex-align-center">
      <DatePicker
        className={`flex1${className ? ` ${className}` : ''}`}
        onChange={onChange}
        hintText={label}
        floatingLabelText={label}
        floatingLabelFixed
        fullWidth
        value={value}
        {...rest}
      />
      <IconButton onClick={e => onChange(e, null)} disabled={!value} tooltip={value && 'Clear'}>
        <ClearIcon />
      </IconButton>
    </div>
  );
};

const TimeField = props => {
  const { className, label, value, onChange, ...rest } = props;
  return (
    <div className="flex flex1 flex-align-center">
      <TimePicker
        className={`flex1${className ? ` ${className}` : ''}`}
        onChange={onChange}
        hintText={label}
        floatingLabelText={label}
        floatingLabelFixed
        value={value}
        {...rest}
      />
      <IconButton onClick={e => onChange(e, null)} disabled={!value} tooltip={value && 'Clear'}>
        <ClearIcon />
      </IconButton>
    </div>
  );
};

class OpportunityBuilderForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = initialState;
  }

  componentDidMount() {
    if (this.props.post) {
      this.setAllFields(this.props.post);
    } else {
      this.setInitialState();
    }
  }

  setInitialState = () => {
    this.setState(initialState);
  };

  setAllFields = post => {
    if (!post.opportunity) {
      return;
    }
    const validFromParsed = parseTimestamp(post.opportunity.validFrom);
    const validToParsed = parseTimestamp(post.opportunity.validTo);
    this.setState({
      // REQUIRED
      modification: true,
      //OPPORTUNITY
      type: post.opportunity.type,
      companyId: post.opportunity.company && post.opportunity.company.id,
      contentUrl: formatText(post.opportunity.contentUrl),
      contentType: post.opportunity.contentType,
      contentLang: post.opportunity.language,
      validFrom: validFromParsed.date,
      validFromTime: validFromParsed.time,
      validTo: validToParsed.date,
      validToTime: validToParsed.time,
      free: post.opportunity.free,
      location: formatText(post.opportunity.location),
      opportunityTags: post.opportunity.tags,
      expandedLocation: !!post.opportunity.location,
      matchConfig: post.opportunity.matchConfig,
      //POST
      publishedAt: post.publishedAt,
      postTitle: formatText(post.title),
      postTags: post.tags,
      mediaUrl: formatText(post.mediaUrl),
      mediaType: post.mediaType,
      postDescription: formatText(post.description),
      displayedFrom: formatDate(post.displayedFrom),
      displayedTo: formatDate(post.displayedTo),
      displayedLang: post.language
    });
  };

  // --- CHANGES ---
  handleOpportunityTypeChange = (e, index, value) => {
    const newValues = { type: value };
    const errors = { ...this.state.errors, ...validateType(newValues) };
    const expandedLocation =
      value === EOpportunityType.EVENT.key ||
      value === EOpportunityType.JOB.key ||
      value === EOpportunityType.TALENT_ACADEMY.key;
    this.setState({
      errors,
      expandedLocation,
      type: value
    });
  };
  handleContentUrlChange = e => {
    this.handleContentUrlSet(e.target.value);
  };
  handleContentUrlSet = contentUrl => {
    const newValues = { contentUrl, contentType: this.state.contentType };
    const errors = { ...this.state.errors, ...validateContentLink(newValues) };
    this.setState({
      errors,
      contentUrl
    });
  };
  handleContentTypeChange = (e, index, value) => {
    const newValues = { contentUrl: this.state.contentUrl, contentType: value };
    const errors = { ...this.state.errors, ...validateContentLink(newValues) };
    this.setState({
      errors,
      contentType: value
    });
  };
  handleContentLangChange = (e, index, value) => {
    const newValues = { contentLang: value };
    const errors = { ...this.state.errors, ...validateContentLanguage(newValues) };
    this.setState({
      errors,
      ...newValues
    });
  };
  getValidityValues = () => {
    return {
      validFrom: this.state.validFrom,
      validTo: this.state.validTo,
      validFromTime: this.state.validFromTime,
      validToTime: this.state.validToTime
    };
  };
  handleValidFromChange = (e, date) => {
    const newValues = { ...this.getValidityValues(), validFrom: date };
    const errors = { ...this.state.errors, ...validateValidityDates(newValues) };
    this.setState({ errors, validFrom: date });
  };
  handleValidFromTimeChange = (e, time) => {
    const newValues = { ...this.getValidityValues(), validFromTime: time };
    const errors = { ...this.state.errors, ...validateValidityDates(newValues) };
    this.setState({ errors, validFromTime: time });
  };
  handleValidToChange = (e, date) => {
    const newValues = { ...this.getValidityValues(), validTo: date };
    const errors = { ...this.state.errors, ...validateValidityDates(newValues) };
    this.setState({ errors, validTo: date });
  };
  handleValidToTimeChange = (e, time) => {
    const newValues = { ...this.getValidityValues(), validToTime: time };
    const errors = { ...this.state.errors, ...validateValidityDates(newValues) };
    this.setState({ errors, validToTime: time });
  };
  handleFreeChange = (e, isInputChecked) => {
    this.setState({
      free: isInputChecked
    });
  };
  handleLocationChange = e => {
    this.setState({ location: e.target.value });
  };
  handleCompanyChange = (e, index, value) => {
    this.setState({ companyId: value });
  };
  handlePostTitleChange = e => {
    const postTitle = e.target.value;
    const errors = { ...this.state.errors, ...validateTitle({ postTitle }) };
    this.setState({ errors, postTitle });
  };
  handleMediaUrlChange = e => {
    this.handleMediaUrlSet(e.target.value);
  };
  handleMediaUrlSet = mediaUrl => {
    const newValues = { mediaUrl, mediaType: this.state.mediaType };
    const errors = { ...this.state.errors, ...validateMediaLink(newValues) };
    this.setState({
      errors,
      mediaUrl
    });
  };

  handleMediaTypeChange = (e, index, value) => {
    const newValues = { mediaUrl: this.state.mediaUrl, mediaType: value };
    const errors = { ...this.state.errors, ...validateMediaLink(newValues) };
    this.setState({
      errors,
      mediaType: value
    });
  };
  handlePostDescriptionSet = postDescription => {
    this.setState({ postDescription });
  };
  handlePostDescriptionChange = e => {
    this.handlePostDescriptionSet(e.target.value);
  };
  handlePurePostDescriptionShow = show => {
    this.setState(({ meta }) => ({ meta: { ...meta, purePostDescriptionShow: show } }));
  };
  handleDisplayedFromChange = (e, date) => {
    const newValues = { displayedTo: this.state.displayedTo, displayedFrom: date };
    const errors = { ...this.state.errors, ...validateDisplayedDates(newValues) };
    this.setState({ errors, displayedFrom: date });
  };
  handleDisplayedToChange = (e, date) => {
    const newValues = { displayedTo: date, displayedFrom: this.state.displayedFrom };
    const errors = { ...this.state.errors, ...validateDisplayedDates(newValues) };
    this.setState({ errors, displayedTo: date });
  };
  handleDisplayedLangChange = (e, index, value) => {
    const newValues = { displayedLang: value };
    const errors = { ...this.state.errors, ...validateLang(newValues) };
    this.setState({
      errors,
      ...newValues
    });
  };
  handlePublishedAtChange = (e, isInputChecked) => {
    this.setState({ publishedAt: isInputChecked ? new Date() : null });
  };

  buildFromState = () => {
    const opportunity = {
      type: this.state.type,
      language: this.state.contentLang,
      company: this.state.companyId ? { id: this.state.companyId } : null,
      contentType: this.state.contentType,
      free: this.state.free,
      contentUrl: parseText(this.state.contentUrl),
      validFrom: toTimestamp(this.state.validFrom, this.state.validFromTime),
      validTo: toTimestamp(this.state.validTo, this.state.validToTime),
      location: parseText(this.state.location),
      tags: this.state.opportunityTags,
      matchConfig: this.state.matchConfig
    };
    return {
      title: parseText(this.state.postTitle),
      language: this.state.displayedLang,
      mediaUrl: parseText(this.state.mediaUrl),
      mediaType: this.state.mediaType,
      description: parseText(this.state.postDescription),
      displayedFrom: this.state.displayedFrom,
      displayedTo: this.state.displayedTo,
      tags: this.state.postTags,
      publishedAt: this.state.publishedAt,
      opportunity
    };
  };

  handleSubmitError = e => {
    this.setState({ submitError: { title: e.message, message: e.response && e.response.body && e.response.body.message } });
  };

  dismissSubmitError = () => this.setState({ submitError: null });

  // --- SUBMIT ---
  handleSubmit = e => {
    e.preventDefault();
    const errors = validate(this.state);
    if (formValid(errors)) {
      const post = this.buildFromState();
      const { history } = this.props;
      const id = this.props.post && this.props.post.id;
      const token = this.props.auth.access_token;
      this.setState({ submitting: true });
      const finalizeReq = () => this.setState({ submitting: false });
      if (!this.state.modification) {
        createOpportunity(post, token)
          .then(() => history.push('/settings/opportunities/'))
          .catch(this.handleSubmitError)
          .finally(finalizeReq)
      } else {
        updateOpportunity(post, id, token)
          .then(() => history.push('/settings/opportunities/'))
          .catch(this.handleSubmitError)
          .finally(finalizeReq);
      }
    } else {
      this.setState({ errors: errors });
    }
  };

  handleOpenPostMediaFileUpload = () => {
    this.setState({
      mediaUploadOpen: true
    });
  };
  handleOpenOpportunityMediaFileUpload = () => {
    this.setState({
      contentUploadOpen: true
    });
  };
  handleCloseMediaUpload = () => {
    this.setState({ mediaUploadOpen: false });
  };
  handleCloseContentUpload = () => {
    this.setState({ contentUploadOpen: false });
  };

  // --- CALLBACK FUNCTIONS ---
  handlePostTagsChange = tag => {
    this.setState({
      postTags: tag
    });
  };
  handleOpportunityTagsChange = tags => {
    this.setState({
      opportunityTags: tags
    });
  };
  handlePreviewShow = () => this.setState({ previewOpen: true });
  handlePreviewHide = () => this.setState({ previewOpen: false });

  render() {
    const { companies } = this.props;
    const { validFrom, validTo, displayedFrom, displayedTo } = this.state;
    const { errors } = this.state;

    return (
      <form autoComplete={'off'} className="Form Opportunity-builder-form" onSubmit={this.handleSubmit} noValidate>
        <div className="flex1" style={{ overflow: 'auto' }}>
          <div className="Form-content-container">
            <div className="container">
              <div className="row">
                <div className="col-lg-6">
                  <Section title="Opportunity">
                    <SectionItem>
                      <SelectField
                        name={'type'}
                        floatingLabelText={'Type'}
                        floatingLabelFixed
                        hintText={'Type'}
                        onChange={this.handleOpportunityTypeChange}
                        value={this.state.type}
                        errorText={errors.type}
                        fullWidth
                      >
                        {renderTypes()}
                      </SelectField>
                      {this.state.expandedLocation && (
                        <TextField
                          name={'location'}
                          onChange={this.handleLocationChange}
                          label={'Location'}
                          meta={{ error: errors.location, touched: !!errors.location }}
                          value={this.state.location}
                          errorText={errors.location}
                          fullWidth
                          multiLine
                        />
                      )}
                    </SectionItem>
                    <SectionItem>
                      <SelectField
                        name={'company'}
                        floatingLabelText={'Company'}
                        floatingLabelFixed
                        hintText={'Company'}
                        onChange={this.handleCompanyChange}
                        value={this.state.companyId}
                        errorText={errors.companyId}
                        fullWidth
                      >
                        {renderCompanies(companies)}
                      </SelectField>
                    </SectionItem>
                    <SectionItem>
                      <SelectField
                        name="contentType"
                        floatingLabelText="Content type"
                        hintText="Content type"
                        floatingLabelFixed
                        onChange={this.handleContentTypeChange}
                        value={this.state.contentType}
                        errorText={errors.contentType}
                        fullWidth
                      >
                        {renderMediaTypes(this.handleOpenOpportunityMediaFileUpload)}
                      </SelectField>
                      <TextField
                        name={'contentUrl'}
                        label={'Link to opportunity content'}
                        style={{ width: '100%' }}
                        onChange={this.handleContentUrlChange}
                        meta={{ error: errors.contentUrl, touched: !!errors.contentUrl }}
                        value={this.state.contentUrl}
                        fullWidth
                        multiLine
                      />
                      <UploadModal
                        open={this.state.contentUploadOpen}
                        onClose={this.handleCloseContentUpload}
                        onUrlSet={this.handleContentUrlSet}
                      />
                    </SectionItem>
                    <SectionItem>
                      <SelectField
                        name={'contentLang'}
                        floatingLabelText={'Opportunity language'}
                        hintText={'Opportunity language'}
                        floatingLabelFixed
                        onChange={this.handleContentLangChange}
                        value={this.state.contentLang}
                        errorText={errors.contentLang}
                        fullWidth
                      >
                        {renderLanguages()}
                      </SelectField>
                    </SectionItem>
                    <SectionItem>
                      <DateField
                        name={'validFrom'}
                        label={'Start on'}
                        value={validFrom || ''}
                        errorText={errors.validFrom}
                        onChange={this.handleValidFromChange}
                      />
                      <TimeField
                        name={'validFromTime'}
                        label={'Start time'}
                        value={this.state.validFromTime || ''}
                        onChange={this.handleValidFromTimeChange}
                      />
                      <DateField
                        name={'validTo'}
                        label={'End on'}
                        value={validTo || ''}
                        errorText={errors.validTo}
                        onChange={this.handleValidToChange}
                      />
                      <TimeField
                        name={'validToTime'}
                        label={'End time'}
                        value={this.state.validToTime || ''}
                        onChange={this.handleValidToTimeChange}
                      />
                    </SectionItem>
                    <SectionItem>
                      <Toggle name={'free'} onToggle={this.handleFreeChange} label={'Free'} toggled={this.state.free} />
                    </SectionItem>
                    <SectionItem>
                      <div className="floating-label">Opportunity tags</div>
                      <OpportunityTags tags={this.state.opportunityTags} onChange={this.handleOpportunityTagsChange} />
                    </SectionItem>
                  </Section>
                </div>
                <div className={'col-lg-6'}>
                  <Section title={'Displayed Info'}>
                    <SectionItem>
                      <Toggle
                        name={'published'}
                        onToggle={this.handlePublishedAtChange}
                        label={'Published'}
                        toggled={!!this.state.publishedAt}
                      />
                    </SectionItem>
                    <SectionItem>
                      <TextField
                        name={'displayedTitle'}
                        label={'Title'}
                        onChange={this.handlePostTitleChange}
                        fullWidth
                        value={this.state.postTitle}
                        meta={{ error: errors.postTitle, touched: !!errors.postTitle }}
                      />
                    </SectionItem>
                    <SectionItem>
                      <SelectField
                        errorText={errors.mediaType}
                        name={'mediaType'}
                        floatingLabelText={'Media type'}
                        hintText={'Media type'}
                        floatingLabelFixed
                        onChange={this.handleMediaTypeChange}
                        fullWidth
                        value={this.state.mediaType}
                      >
                        {renderMediaTypes(
                          this.handleOpenPostMediaFileUpload,
                          t => t === EMediaType.IMAGE || t === EMediaType.VIDEO
                        )}
                      </SelectField>
                      <TextField
                        meta={{ error: errors.mediaUrl, touched: !!errors.mediaUrl }}
                        name={'mediaUrl'}
                        onChange={this.handleMediaUrlChange}
                        label={'Link to media'}
                        fullWidth
                        multiLine
                        value={this.state.mediaUrl}
                      />
                      <UploadModal
                        open={this.state.mediaUploadOpen}
                        onClose={this.handleCloseMediaUpload}
                        onUrlSet={this.handleMediaUrlSet}
                      />
                      <MediaPreview type={this.state.mediaType} url={this.state.mediaUrl} />
                    </SectionItem>
                    <SectionItem>
                      <div className="floating-label">Description</div>
                      <RichTextEditor value={this.state.postDescription} onChange={this.handlePostDescriptionSet} />
                      {renderShowPlainDescription(
                        this.state.meta.purePostDescriptionShow,
                        this.handlePurePostDescriptionShow
                      )}
                      {this.state.meta.purePostDescriptionShow && (
                        <TextField
                          name={'displayedDescription'}
                          multiLine
                          fullWidth
                          onChange={this.handlePostDescriptionChange}
                          value={this.state.postDescription}
                          meta={{ error: errors.postDescription, touched: !!errors.postDescription }}
                        />
                      )}
                    </SectionItem>
                    <SectionItem>
                      <SelectField
                        name={'displayedLang'}
                        floatingLabelText={'Language'}
                        floatingLabelFixed
                        hintText={'Language'}
                        onChange={this.handleDisplayedLangChange}
                        value={this.state.displayedLang}
                        errorText={errors.displayedLang}
                        fullWidth
                      >
                        {renderLanguages()}
                      </SelectField>
                    </SectionItem>
                    <SectionItem>
                      <DateField
                        name={'displayedFrom'}
                        label={'Displayed from'}
                        value={displayedFrom || ''}
                        errorText={errors.displayedFrom}
                        onChange={this.handleDisplayedFromChange}
                      />
                      <DateField
                        name={'displayedTo'}
                        label={'Displayed to'}
                        value={displayedTo || ''}
                        errorText={errors.displayedTo}
                        onChange={this.handleDisplayedToChange}
                      />
                    </SectionItem>
                    <SectionItem>
                      <TextTag
                        hintText="Add a tag..."
                        label="Tags"
                        errorText={errors.postTags}
                        onChange={this.handlePostTagsChange}
                        chips={this.state.postTags}
                      />
                    </SectionItem>
                  </Section>
                </div>
              </div>
            </div>
          </div>
        </div>
        {this.state.previewOpen && (
          <Overlay onRequestClose={this.handlePreviewHide}>{renderOverlayContent(this.buildFromState())}</Overlay>
        )}
        {this.state.submitError && (
          <div className="bg-danger flex" style={{ color: 'white' }}>
            <div className="flex1 padding-1em">
              <div>{this.state.submitError.title}</div>
              {this.state.submitError.message && (
                <div><small>{this.state.submitError.message}</small></div>
              )}
            </div>
            <div>
              <IconButton
                onClick={this.dismissSubmitError}
                tooltip={'Close'}
              >
                <ClearIcon color="white" />
              </IconButton>
            </div>
          </div>
        )}
        <div className={'Form-buttons-container'}>
          <div className="Form-buttons clearfix">
            <div className="float-left" style={{ fontSize: 0 }}>
              <Button
                label="Back"
                icon={<BackIcon />}
                style={{ fontSize: '16px' }}
                containerElement={<Link to="/settings/opportunities/" />}
              />
            </div>
            <div className={'float-right'}>
              <Button
                secondary
                label={'Preview'}
                onClick={this.handlePreviewShow}
                style={{ marginRight: '8px' }}
                icon={<PreviewIcon />}
              />
              <Button raised primary label={'Save'} type="submit" loading={this.state.submitting} />
            </div>
          </div>
        </div>
      </form>
    );
  }
}

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(withRouter(OpportunityBuilderForm));



// WEBPACK FOOTER //
// ./src/components/forms/OpportunityBuilderForm.js